import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  Container
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import AuthViewController from "../redux/actions/AuthViewController";

// ✅ Background image (use your own asset)
import ResumeImg from "../assets/women-8080878_1280.png";
import logo from "../assets/Thryiv_logo_tm_TB.png"

const ResetPasswordPage = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useDispatch();
  const { resetPassword } = AuthViewController();
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const loading = useSelector((state) => state.loading);

  const onSubmit = async (data) => {
    try {
      await dispatch(resetPassword(uid, token, { password: data.password }));
      navigate("/login"); // ✅ redirect after success
    } catch (error) {
      console.error("Reset failed", error);
    }
  };

  return (
    <Grid container sx={{ height: "100vh", width: "100vw" }}>
      {/* LEFT PANEL */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          position: "relative",
          color: "#fff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          textAlign: "center",
          p: 4,
          minHeight: "50vh",
          backgroundImage: `url(${ResumeImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0, 0, 0, 0.6)",
            zIndex: 1
          }}
        />

        {/* Content */}
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" } }}
          >
            Secure your account with a new password.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 2,
              maxWidth: "80%",
              fontSize: { xs: "0.9rem", md: "1rem" }
            }}
          >
            Reset your password quickly and continue managing your HR tasks
            seamlessly.
          </Typography>
        </Box>
      </Grid>

      {/* RIGHT PANEL */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          backgroundColor: "#fff",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, md: 4 }
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Paper
            sx={{
              p: 4,
              width: "100%",
              boxShadow: 3,
              borderRadius: 2
            }}
          >
            {/* Logo / Heading */}
            <Box display="flex" justifyContent="center" alignItems="center">
                          <img
                            src={logo}
                            alt="Company Logo"
                            style={{
                              height: 60,           // adjust height as needed
                              objectFit: "contain", // keeps aspect ratio
                            }}
                          />
            </Box>

            <Typography
              variant="h6"
              align="center"
              sx={{ mt: 2, fontWeight: "bold", mb: 3 }}
            >
              Reset Password
            </Typography>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* New Password */}
              <Controller
                name="password"
                control={control}
                rules={{
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters long"
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="New Password"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />

              {/* Confirm Password */}
              <Controller
                name="confirm_password"
                control={control}
                rules={{
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === watch("password") || "Passwords do not match"
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirm Password"
                    fullWidth
                    type={showConfirmPassword ? "text" : "password"}
                    error={!!errors.confirm_password}
                    helperText={errors.confirm_password?.message}
                    variant="outlined"
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword((prev) => !prev)
                            }
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: "#FF6A00",
                  "&:hover": { backgroundColor: "#E65A00" },
                  py: 1.4
                }}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>

              {/* Footer */}
              <Typography
                variant="caption"
                display="block"
                align="center"
                sx={{ mt: 4, color: "text.secondary" }}
              >
                Copyright © 2025 - Swatsan Tech Pvt. Ltd.
              </Typography>
            </form>
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ResetPasswordPage;
